<template>
  <section class="signal-section px-4 sm:px-6 lg:px-8" ref="sectionRef">
    <div class="max-w-3xl mx-auto">
      <header class="signal-header">
        <p class="signal-kicker">Signal intercept</p>
        <h2 class="signal-title">Wire the payment rail</h2>
        <p class="signal-desc">
          Tap each stage in processing order. Complete the chain to decrypt the payload.
        </p>
      </header>

      <div class="signal-panel" :class="{ unlocked: isUnlocked, fault: faultFlash }">
        <div class="scan-line" aria-hidden="true"></div>
        <div class="panel-top">
          <span class="status-dot" :class="isUnlocked ? 'live' : 'armed'"></span>
          <span class="panel-label">ledger_link // sandbox_v3</span>
          <span class="progress-tag">{{ progressLabel }}</span>
        </div>

        <!-- Active game -->
        <div v-if="!isUnlocked" ref="gameRef" class="game-board">
          <svg class="wire-layer" ref="svgRef">
            <defs>
              <linearGradient id="wireGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#2dd4bf" />
                <stop offset="100%" stop-color="#60a5fa" />
              </linearGradient>
            </defs>
            <path
              v-for="wire in wires"
              :key="wire.id"
              :d="wire.d"
              class="wire-path"
              :class="{ lit: wire.lit }"
              :style="{ strokeDasharray: wire.length, strokeDashoffset: wire.length }"
            />
            <circle
              v-if="sparkVisible"
              ref="sparkRef"
              r="4"
              class="spark"
              fill="#5eead4"
            />
          </svg>

          <button
            v-for="node in shuffledNodes"
            :key="node.id"
            :ref="(el) => setNodeRef(node.id, el)"
            type="button"
            class="node"
            :class="nodeClass(node.id)"
            :disabled="lockedIds.has(node.id)"
            @click="tapNode(node.id)"
          >
            <span class="node-icon"><i :class="`fas ${node.icon}`"></i></span>
            <span class="node-label">{{ node.label }}</span>
            <span class="node-code">{{ node.code }}</span>
          </button>
        </div>

        <!-- Decrypt readout -->
        <div v-else class="decrypt-zone">
          <div class="hex-stream" aria-hidden="true">
            <span v-for="(c, i) in hexDisplay" :key="i" class="hex-char">{{ c }}</span>
          </div>
          <p ref="phraseRef" class="clearance-phrase">{{ revealedPhrase }}</p>
          <p class="clearance-sub">{{ clearanceSub }}</p>
          <div class="clearance-badge">
            <span class="badge-dot"></span>
            BUILDER CLEARANCE GRANTED
          </div>
          <router-link to="/contact" class="clearance-cta">
            Decode complete — say hello
            <i class="fas fa-arrow-right text-xs"></i>
          </router-link>
        </div>

        <div class="panel-footer">
          <p v-if="!isUnlocked && lastError" class="fault-msg">{{ lastError }}</p>
          <p v-else-if="!isUnlocked" class="hint-msg">
            Hint: money moves <strong>originate → authorize → settle → confirm</strong>
          </p>
          <button
            v-if="!isUnlocked && progress > 0"
            type="button"
            class="reset-btn"
            @click="resetGame"
          >
            Reset chain
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { gsap } from 'gsap';

const STORAGE_KEY = 'jeromejjb-signal-intercept';

const FLOW = ['originate', 'authorize', 'settle', 'confirm'];

const NODES = [
  { id: 'originate', label: 'Originate', code: 'ORG', icon: 'fa-right-to-bracket' },
  { id: 'authorize', label: 'Authorize', code: 'AUTH', icon: 'fa-shield-halved' },
  { id: 'settle', label: 'Settle', code: 'STL', icon: 'fa-coins' },
  { id: 'confirm', label: 'Confirm', code: 'ACK', icon: 'fa-circle-check' },
];

const PHRASE = 'SHIP THE RIGHT THING';
const SUB = 'You wired a payment rail. I wire ideas into products.';

const sectionRef = ref(null);
const gameRef = ref(null);
const svgRef = ref(null);
const sparkRef = ref(null);
const phraseRef = ref(null);
const nodeEls = ref({});
const shuffledNodes = ref([]);
const sequence = ref([]);
const lockedIds = ref(new Set());
const wires = ref([]);
const sparkVisible = ref(false);
const isUnlocked = ref(false);
const faultFlash = ref(false);
const lastError = ref('');
const revealedPhrase = ref('');
const hexDisplay = ref([]);

let hexInterval = null;

const progress = computed(() => sequence.value.length);

const progressLabel = computed(() => {
  if (isUnlocked.value) return 'DECRYPTED';
  return `${progress.value}/${FLOW.length} LINKED`;
});

const setNodeRef = (id, el) => {
  if (el) nodeEls.value[id] = el;
};

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const nodeClass = (id) => ({
  locked: lockedIds.value.has(id),
  next: !lockedIds.value.has(id) && progress.value < FLOW.length,
  fault: false,
});

const getCenter = (el, container) => {
  const r = el.getBoundingClientRect();
  const c = container.getBoundingClientRect();
  return { x: r.left - c.left + r.width / 2, y: r.top - c.top + r.height / 2 };
};

const buildWire = (fromId, toId) => {
  const container = gameRef.value;
  const fromEl = nodeEls.value[fromId];
  const toEl = nodeEls.value[toId];
  if (!container || !fromEl || !toEl) return null;

  const a = getCenter(fromEl, container);
  const b = getCenter(toEl, container);
  const mx = (a.x + b.x) / 2;
  const d = `M ${a.x} ${a.y} Q ${mx} ${a.y - 28} ${b.x} ${b.y}`;
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', d);
  const length = path.getTotalLength();

  return { id: `${fromId}-${toId}`, d, length, lit: false };
};

const updateWires = () => {
  const newWires = [];
  for (let i = 1; i < sequence.value.length; i += 1) {
    const w = buildWire(sequence.value[i - 1], sequence.value[i]);
    if (w) {
      w.lit = true;
      newWires.push(w);
    }
  }
  wires.value = newWires;
};

const animateWire = (wireIndex) => {
  nextTick(() => {
    const paths = svgRef.value?.querySelectorAll('.wire-path');
    const path = paths?.[wireIndex];
    if (!path) return;

    const len = wires.value[wireIndex]?.length || 100;
    gsap.fromTo(
      path,
      { strokeDashoffset: len, opacity: 0.3 },
      { strokeDashoffset: 0, opacity: 1, duration: 0.55, ease: 'power2.out' }
    );

    sparkVisible.value = true;
    const wire = wires.value[wireIndex];
    if (sparkRef.value && wire) {
      const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      pathEl.setAttribute('d', wire.d);
      const total = pathEl.getTotalLength();
      const obj = { t: 0 };
      gsap.to(obj, {
        t: total,
        duration: 0.55,
        ease: 'power2.out',
        onUpdate: () => {
          const pt = pathEl.getPointAtLength(obj.t);
          sparkRef.value.setAttribute('cx', pt.x);
          sparkRef.value.setAttribute('cy', pt.y);
        },
        onComplete: () => {
          sparkVisible.value = false;
        },
      });
    }
  });
};

const flashFault = (msg) => {
  lastError.value = msg;
  faultFlash.value = true;
  gsap.fromTo(
    gameRef.value,
    { x: -6 },
    { x: 0, duration: 0.45, ease: 'elastic.out(1, 0.6)' }
  );
  setTimeout(() => {
    faultFlash.value = false;
  }, 400);
};

const resetGame = () => {
  sequence.value = [];
  lockedIds.value = new Set();
  wires.value = [];
  lastError.value = '';
  sparkVisible.value = false;
};

const runDecrypt = () => {
  isUnlocked.value = true;
  localStorage.setItem(STORAGE_KEY, '1');
  revealedPhrase.value = '';

  const chars = '0123456789ABCDEF'.split('');
  hexDisplay.value = Array.from({ length: 32 }, () => chars[Math.floor(Math.random() * 16)]);

  hexInterval = setInterval(() => {
    hexDisplay.value = hexDisplay.value.map(() => chars[Math.floor(Math.random() * 16)]);
  }, 60);

  setTimeout(() => {
    clearInterval(hexInterval);
    hexDisplay.value = [];
  }, 1400);

  let i = 0;
  const typeTimer = setInterval(() => {
    revealedPhrase.value += PHRASE[i];
    i += 1;
    if (i >= PHRASE.length) {
      clearInterval(typeTimer);
      nextTick(() => {
        gsap.from(phraseRef.value, {
          textShadow: '0 0 30px rgba(45,212,191,0.8)',
          duration: 0.6,
          ease: 'power2.out',
        });
      });
    }
  }, 70);
};

const tapNode = async (id) => {
  if (lockedIds.value.has(id) || isUnlocked.value) return;

  const expected = FLOW[progress.value];
  if (id !== expected) {
    flashFault(`✗ Invalid sequence. Expected ${NODES.find((n) => n.id === expected)?.label}. Chain reset.`);
    resetGame();
    return;
  }

  lastError.value = '';
  sequence.value = [...sequence.value, id];
  lockedIds.value = new Set(sequence.value);

  const el = nodeEls.value[id];
  gsap.fromTo(el, { scale: 0.92 }, { scale: 1, duration: 0.45, ease: 'back.out(2)' });

  updateWires();
  if (progress.value > 1) {
    animateWire(progress.value - 2);
  }

  if (progress.value === FLOW.length) {
    await new Promise((r) => setTimeout(r, 700));
    runDecrypt();
  }
};

const clearanceSub = computed(() => SUB);

const onResize = () => {
  if (!isUnlocked.value && sequence.value.length > 1) updateWires();
};

onMounted(() => {
  shuffledNodes.value = shuffle(NODES);

  if (localStorage.getItem(STORAGE_KEY)) {
    isUnlocked.value = true;
    revealedPhrase.value = PHRASE;
  }

  gsap.from(sectionRef.value, {
    y: 36,
    opacity: 0,
    duration: 0.75,
    ease: 'power3.out',
  });

  window.addEventListener('resize', onResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
  if (hexInterval) clearInterval(hexInterval);
});

watch(sequence, () => nextTick(updateWires), { deep: true });
</script>

<style scoped>
.signal-section {
  padding: 1rem 1rem 3rem;
  position: relative;
  z-index: 10;
}

.signal-header {
  text-align: center;
  margin-bottom: 1.75rem;
}

.signal-kicker {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #2dd4bf;
  margin-bottom: 0.5rem;
}

.signal-title {
  font-size: clamp(1.35rem, 4vw, 1.75rem);
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.5rem;
}

.signal-desc {
  font-size: 0.9375rem;
  color: #9ca3af;
  max-width: 26rem;
  margin: 0 auto;
  line-height: 1.5;
}

.signal-panel {
  position: relative;
  border-radius: 1rem;
  overflow: hidden;
  background: linear-gradient(165deg, rgba(15, 23, 42, 0.95) 0%, rgba(8, 12, 22, 0.98) 100%);
  border: 1px solid rgba(45, 212, 191, 0.15);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
}

.signal-panel.unlocked {
  border-color: rgba(45, 212, 191, 0.4);
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.4),
    0 0 80px rgba(45, 212, 191, 0.08);
}

.signal-panel.fault {
  border-color: rgba(248, 113, 113, 0.45);
}

.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(45, 212, 191, 0.6), transparent);
  animation: scan 3.5s linear infinite;
  pointer-events: none;
  z-index: 2;
}

@keyframes scan {
  0% { transform: translateX(-100%); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateX(100%); opacity: 0; }
}

.panel-top {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.6875rem;
  color: #6b7280;
}

.status-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.armed {
  background: #eab308;
  box-shadow: 0 0 8px rgba(234, 179, 8, 0.6);
  animation: pulse-dot 2s ease infinite;
}

.status-dot.live {
  background: #22c55e;
  box-shadow: 0 0 10px rgba(34, 197, 94, 0.7);
}

@keyframes pulse-dot {
  50% { opacity: 0.5; }
}

.panel-label {
  flex: 1;
}

.progress-tag {
  color: #2dd4bf;
  font-weight: 600;
}

.game-board {
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  padding: 1.5rem 1rem 1.25rem;
  min-height: 220px;
}

@media (min-width: 640px) {
  .game-board {
    grid-template-columns: repeat(4, 1fr);
    gap: 0.75rem;
    padding: 2rem 1.25rem 1.5rem;
  }
}

.wire-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.wire-path {
  fill: none;
  stroke: url(#wireGrad);
  stroke-width: 2;
  stroke-linecap: round;
  opacity: 0.35;
  filter: drop-shadow(0 0 4px rgba(45, 212, 191, 0.5));
}

.wire-path.lit {
  opacity: 1;
}

.spark {
  filter: drop-shadow(0 0 6px #5eead4);
}

.node {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  padding: 1rem 0.5rem;
  border-radius: 0.875rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #d1d5db;
  cursor: pointer;
  transition:
    border-color 0.25s ease,
    background 0.25s ease,
    box-shadow 0.25s ease,
    transform 0.2s ease;
}

.node:hover:not(:disabled) {
  border-color: rgba(45, 212, 191, 0.4);
  background: rgba(45, 212, 191, 0.06);
  transform: translateY(-2px);
}

.node.locked {
  border-color: rgba(45, 212, 191, 0.55);
  background: rgba(45, 212, 191, 0.1);
  box-shadow: 0 0 24px rgba(45, 212, 191, 0.12);
  color: #ecfdf5;
  cursor: default;
}

.node-icon {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  color: #2dd4bf;
  font-size: 0.95rem;
}

.node.locked .node-icon {
  background: rgba(45, 212, 191, 0.15);
}

.node-label {
  font-size: 0.8125rem;
  font-weight: 600;
}

.node-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.625rem;
  color: #6b7280;
  letter-spacing: 0.08em;
}

.node.locked .node-code {
  color: #5eead4;
}

.decrypt-zone {
  padding: 2rem 1.25rem 1.5rem;
  text-align: center;
}

.hex-stream {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.6875rem;
  color: #374151;
  letter-spacing: 0.15em;
  margin-bottom: 1.25rem;
  word-break: break-all;
  line-height: 1.8;
}

.hex-char {
  display: inline-block;
}

.clearance-phrase {
  font-size: clamp(1.25rem, 4vw, 1.75rem);
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: linear-gradient(90deg, #fff 0%, #99f6e4 50%, #60a5fa 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin: 0 0 0.75rem;
  min-height: 2.5rem;
}

.clearance-sub {
  font-size: 0.9375rem;
  color: #9ca3af;
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.clearance-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.875rem;
  border-radius: 9999px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #2dd4bf;
  border: 1px solid rgba(45, 212, 191, 0.35);
  background: rgba(45, 212, 191, 0.08);
  margin-bottom: 1.25rem;
}

.badge-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: #2dd4bf;
  box-shadow: 0 0 8px #2dd4bf;
}

.clearance-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  background: #2dd4bf;
  padding: 0.625rem 1.25rem;
  border-radius: 9999px;
  text-decoration: none;
  transition: background 0.2s ease;
}

.clearance-cta:hover {
  background: #5eead4;
}

.panel-footer {
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.hint-msg,
.fault-msg {
  font-size: 0.75rem;
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.hint-msg {
  color: #6b7280;
}

.hint-msg strong {
  color: #9ca3af;
  font-weight: 600;
}

.fault-msg {
  color: #f87171;
}

.reset-btn {
  font-size: 0.6875rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  color: #9ca3af;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  padding: 0.35rem 0.625rem;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.reset-btn:hover {
  color: #e5e7eb;
  border-color: rgba(255, 255, 255, 0.2);
}

@media (prefers-reduced-motion: reduce) {
  .scan-line {
    animation: none;
    opacity: 0.3;
  }

  .status-dot.armed {
    animation: none;
  }
}
</style>
